/**
 * Runtime environment detection for the public booking app.
 *
 * The point of this module is to know, at runtime, whether the app is running
 * against STAGING so we can render an unmistakable warning (see
 * `components/EnvironmentBanner.tsx`). Customers (and counter staff) were
 * creating real bookings on the staging site without noticing the URL — staging
 * and production looked identical.
 *
 * Hostname is the PRIMARY signal because the wrong hostname *is* the bug, and it
 * stays correct even when `VITE_API_URL` is unset (a staging build missing that
 * var falls back to the prod backend). `VITE_API_URL` is only a secondary guard
 * for preview hostnames (e.g. `*.vercel.app`) that don't carry `-staging.`.
 */

export type AppEnvironment = "development" | "staging" | "production";

/**
 * Pure environment classifier — no `window`/`import.meta` access so it can be
 * unit-tested directly with explicit inputs.
 */
export function classifyEnvironment(
  hostname: string,
  viteApiUrl?: string,
): AppEnvironment {
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local")
  ) {
    return "development";
  }

  // Public booking staging: `book-staging.rutalink.cloud`
  // Admin staging (shared check): `<tenant>.staging.rutalink.cloud`
  if (
    hostname.includes(".staging.rutalink.cloud") ||
    hostname.includes("-staging.rutalink.cloud")
  ) {
    return "staging";
  }

  // Secondary guard for preview/misconfigured hosts without a `-staging` segment.
  if ((viteApiUrl ?? "").includes("staging")) {
    return "staging";
  }

  return "production";
}

/**
 * Pure mapping from a staging location to its production equivalent, preserving
 * path + query + hash. Non-staging hosts pass through unchanged.
 */
export function toProductionUrl(
  hostname: string,
  pathname: string,
  search: string,
  hash: string,
): string {
  const prodHost = hostname
    .replace(".staging.rutalink.cloud", ".rutalink.cloud")
    .replace("-staging.rutalink.cloud", ".rutalink.cloud");
  return `https://${prodHost}${pathname}${search}${hash}`;
}

/** Browser-only: classify the current window's environment. */
export function getEnvironment(): AppEnvironment {
  if (typeof window === "undefined") return "production";
  return classifyEnvironment(
    window.location.hostname,
    import.meta.env.VITE_API_URL as string | undefined,
  );
}

/** Browser-only: the production URL matching the current page. */
export function productionUrl(): string {
  const { hostname, pathname, search, hash } = window.location;
  return toProductionUrl(hostname, pathname, search, hash);
}
